import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type ThemeMode = 'light' | 'dark' | 'system';

interface DarkModeContextType {
  theme: ThemeMode;
  isDarkMode: boolean;
  setTheme: (theme: ThemeMode) => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

function getSystemPreference(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function DarkModeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
    // Backward compatibility: check old darkMode key
    const oldDarkMode = localStorage.getItem('darkMode');
    if (oldDarkMode !== null) {
      localStorage.removeItem('darkMode');
      return JSON.parse(oldDarkMode) ? 'dark' : 'light';
    }
    return 'light';
  });

  const [systemPreference, setSystemPreference] = useState(getSystemPreference);

  // Compute effective dark mode
  const isDarkMode = theme === 'system' ? systemPreference : theme === 'dark';

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document and native status bar
  useEffect(() => {
    localStorage.setItem('themeMode', theme);
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Update native status bar style on Android/iOS
    if (Capacitor.isNativePlatform()) {
      // setStyle is the only cross-platform method here. setOverlaysWebView and
      // setBackgroundColor are Android-only and are unimplemented on iOS — the
      // simulator log shows them being called and doing nothing. iOS gets
      // edge-to-edge layout from viewport-fit=cover instead.
      StatusBar.setStyle({ style: isDarkMode ? Style.Dark : Style.Light });

      if (Capacitor.getPlatform() === 'android') {
        StatusBar.setOverlaysWebView({ overlay: true });
        StatusBar.setBackgroundColor({ color: '#00000000' });
      }
    }
  }, [theme, isDarkMode]);

  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeState(newTheme);
  }, []);

  return (
    <DarkModeContext.Provider value={{ theme, isDarkMode, setTheme }}>
      {children}
    </DarkModeContext.Provider>
  );
}

export function useDarkMode() {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
}
